import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FiMessageSquare, FiCheckCircle, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import ImageSlider from '../components/ImageSlider';
import SEO from '../components/SEO';
import { keyframes } from 'styled-components';

const zoomIn = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1.1);
  }
`;

const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${props => props.theme.colors.white};
  overflow: hidden;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-height: 80vh;
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(26, 77, 128, 0.8) 0%, rgba(44, 62, 80, 0.7) 100%), url(${props => props.currentImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  animation: ${zoomIn} 10s ease-in-out infinite;
  transition: opacity 0.5s ease-in-out;
  opacity: ${props => props.isActive ? 1 : 0};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    background-attachment: scroll;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SliderNavigation = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 3;
`;

const SliderDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${props => props.theme.colors.white};
  background: ${props => props.isActive ? props.theme.colors.white : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.white};
    transform: scale(1.2);
  }
`;

const SliderArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: ${props => props.theme.colors.white};
  font-size: 1.5rem;
  padding: 15px;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.3s ease;
  z-index: 3;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: 20px;
  }
  
  &.next {
    right: 20px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 10px;
    font-size: 1.2rem;
    
    &.prev {
      left: 10px;
    }
    
    &.next {
      right: 10px;
    }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  color: #ffffff;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 40px;
  opacity: 0.95;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.5);
  font-family: 'Times New Roman', Times, serif;
  color: ${props => props.theme.colors.white};
  font-weight: 600;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.3rem;
  }
`;

const Section = styled.section`
  padding: 80px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 60px;
  color: ${props => props.theme.colors.primary};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.secondary};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
    margin-bottom: 40px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const FeatureCard = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 20px;
`;

const ServiceItem = styled(motion.p)`
  margin: 0 0 12px 0;
  font-size: 1.2rem;
  color: #333;
  font-weight: 600;
  font-family: 'Times New Roman', Times, serif;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  border-radius: 6px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &::before {
    content: '●';
    color: ${props => props.theme.colors.primary};
    margin-right: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.4);
    transform: translateX(8px);
    color: ${props => props.theme.colors.primary};
    font-weight: 700;
    
    &::before {
      transform: scale(1.3);
      color: ${props => props.theme.colors.secondary};
    }
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;
  margin-top: 40px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const ServicesColumn = styled(motion.div)`
  background: #87b3c596;
  width: 100%;
  height: auto;
  border-radius: 8px;
  padding: 20px 20px;
  box-shadow: 0px 12px 20px -8px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0px 15px 25px -10px rgba(0, 0, 0, 0.4);
    border-color: ${props => props.theme.colors.primary};
    background: rgba(135, 179, 197, 0.9);
  }
  
  &:nth-child(1), &:nth-child(3) {
    padding: 15px 15px;
    max-height: none;
    overflow-y: visible;
  }
`;



const ServiceIcon = styled.span`
  font-size: 1rem;
  min-width: 20px;
  color: ${props => props.theme.colors.primary};
  margin-right: 10px;
`;

const ServiceText = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #333;
  font-weight: 500;
  font-family: 'Times New Roman', Times, serif;
  display: flex;
  align-items: center;
  
  &::before {
    content: '●';
    color: ${props => props.theme.colors.primary};
    margin-right: 10px;
    font-size: 0.8rem;
  }
`;

const GallerySection = styled.section`
  padding: 80px 0;
  background: #f9f8f8;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const GalleryTitle = styled(motion.h3)`
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 60px;
  color: ${props => props.theme.colors.primary};
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: #f39c12;
    margin: 20px auto 0;
    border-radius: 0;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const FilterContainer = styled.ul`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 10px;
  }
`;

const FilterItem = styled.li`
  &.port-filter.all.active a,
  &.cat-item-1.active a,
  &.cat-item-2.active a,
  &.cat-item-3.active a,
  &.cat-item-4.active a {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FilterLink = styled.a`
  padding: 10px 20px;
  border: 2px solid ${props => props.theme.colors.primary};
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PortfolioArea = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  list-style: none;
  padding: 0;
  margin: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
`;

const PortfolioItem = styled(motion.li)`
  position: relative;
  border-radius: 0;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
    
    .port-info {
      opacity: 1;
    }
  }
  
  img {
    width: 100%;
    height: 180px;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.1);
  }
`;

const ImageBlock = styled.span`
  position: relative;
  display: block;
  
  .port-info {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(26, 77, 128, 0.8);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    
    h5 {
      color: white;
      text-align: center;
      font-size: 1.2rem;
      margin: 0 0 10px 0;
    }
    
    p {
      color: white;
      text-align: center;
      font-size: 1rem;
      margin: 0;
      font-family: 'Times New Roman', Times, serif;
    }
  }
`;

const ImageZoom = styled.a`
  display: block;
  text-decoration: none;
`;



const StatsTestSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.dark});
  padding: 80px 0;
  color: ${props => props.theme.colors.white};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const StatsCard = styled(motion.div)`
  text-align: center;
  padding: 30px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const StatsContent = styled.div`
  i {
    font-size: 3rem;
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 20px;
    display: block;
  }
  
  .counter {
    font-size: 2.5rem;
    font-weight: 700;
    color: ${props => props.theme.colors.white};
    margin-bottom: 10px;
  }
  
  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: ${props => props.theme.colors.white};
    margin: 0;
  }
`;

const TestimonialsSection = styled.section`
  background: ${props => props.theme.colors.white};
  padding: 80px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const TestimonialsCarousel = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
`;

const TestimonialCard = styled(motion.div)`
  background: ${props => props.theme.colors.light};
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
  position: relative;
  
  &::before {
    content: '"';
    font-size: 4rem;
    color: ${props => props.theme.colors.secondary};
    position: absolute;
    top: 10px;
    left: 20px;
    font-family: serif;
  }
`;

const EnquirySection = styled.section`
  background: ${props => props.theme.colors.light};
  padding: 80px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const EnquiryForm = styled.form`
  max-width: 600px;
  margin: 0 auto;
  background: ${props => props.theme.colors.white};
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const WhyChooseSection = styled.section`
  padding: 50px 0;
  background: #f8f9fa;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 40px 0;
  }
`;

const WhyChooseContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  max-width: 500px;
  margin: 0;
  padding-left: 50px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 20px;
    width: 2px;
    height: 100%;
    background: linear-gradient(to bottom, #1a4d80, #f39c12, #1a4d80);
    z-index: 1;
    animation: timelineGlow 3s ease-in-out infinite;
  }

  @keyframes timelineGlow {
    0%, 100% { box-shadow: 0 0 5px rgba(26, 77, 128, 0.3); }
    50% { box-shadow: 0 0 15px rgba(26, 77, 128, 0.6); }
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding-left: 30px;
    max-width: 100%;
    
    &::before {
      left: 15px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  margin-bottom: 25px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineContent = styled.div`
  background: white;
  padding: 15px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  margin-left: 20px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 4px solid transparent;
  
  &:hover {
    transform: translateX(10px) scale(1.02);
    box-shadow: 0 8px 25px rgba(26, 77, 128, 0.15);
    border-left-color: #f39c12;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 15px;
    left: -10px;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid white;
    transition: all 0.3s ease;
  }
  
  &:hover::before {
    border-right-color: #f8f9fa;
    transform: scale(1.1);
  }
`;

const TimelineTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 8px;
  transition: all 0.3s ease;
  
  ${TimelineContent}:hover & {
    color: #f39c12;
    transform: translateX(5px);
  }
`;

const TimelineDescription = styled.p`
  font-size: 0.9rem;
  color: #333;
  line-height: 1.4;
  margin-bottom: 0;
  transition: all 0.3s ease;
  
  ${TimelineContent}:hover & {
    color: #555;
    transform: translateX(3px);
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  top: 15px;
  left: -40px;
  width: 12px;
  height: 12px;
  background: #1a4d80;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 0 0 4px #1a4d80;
  z-index: 2;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: dotPulse 2s ease-in-out infinite;
  
  @keyframes dotPulse {
    0%, 100% { 
      transform: scale(1);
      box-shadow: 0 0 0 4px #1a4d80;
    }
    50% { 
      transform: scale(1.2);
      box-shadow: 0 0 0 8px rgba(26, 77, 128, 0.3);
    }
  }
  
  ${TimelineItem}:hover & {
    background: #f39c12;
    transform: scale(1.3);
    box-shadow: 0 0 0 6px #f39c12, 0 0 20px rgba(243, 156, 18, 0.4);
    animation: none;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    left: -30px;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    order: -1;
  }
`;

const WhyChooseImage = styled.img`
  max-width: 80%;
  height: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    max-width: 60%;
  }
`;

const FormField = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
  }
  
  input, textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 1rem;
    font-family: inherit;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.secondary};
      box-shadow: 0 0 5px rgba(243, 156, 18, 0.3);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

// Additional Enquiry Form Styled Components
const EnquiryInput = styled.input`
  width: 100%;
  padding: 15px;
  border: 1px solid #dee2e6;
  border-radius: 0;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: #1a4d80;
  }
`;

const EnquiryTextArea = styled.textarea`
  width: 100%;
  padding: 15px;
  border: 1px solid #dee2e6;
  border-radius: 0;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.3s ease;
  resize: vertical;
  font-family: inherit;
  min-height: 120px;
  
  &:focus {
    border-color: #1a4d80;
  }
`;

const EnquirySubmitButton = styled.button`
  padding: 15px 30px;
  background-color: #1a4d80;
  color: white;
  border: none;
  border-radius: 0;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: #2c5aa0;
    transform: translateY(-2px);
  }
`;

const EnquirySuccessMessage = styled.p`
  color: red;
  text-align: center;
  margin: 10px 0 0 0;
  font-size: 0.9rem;
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 15px 30px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background: ${props => props.theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px;
  border-radius: 5px;
  margin-top: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

// Features data
const featuresData = [
  {
    icon: '🏗️',
    title: 'Premium Tent Solutions',
    description: 'High-quality tents and structures for all types of events and celebrations.'
  },
  {
    icon: '🎉',
    title: 'Event Management',
    description: 'Complete event planning and management services for weddings, corporate events, and exhibitions.'
  },
  {
    icon: '🏢',
    title: 'Exhibition Services',
    description: 'Professional exhibition setup and infrastructure for trade shows and business events.'
  }
];

// Hero slider images
const heroImages = [
  '/IMG-20250802-WA0077.jpg',
  '/IMG-20250802-WA0056.jpg',
  '/IMG-20250802-WA0076.jpg',
  '/IMG-20250802-WA0062.jpg',
  '/IMG-20250802-WA0055.jpg'
];

// Gallery data
const galleryData = [
  // Warehousing
  { id: 1, category: 'Warehousing', image: '/IMG-20250802-WA0079.jpg', alt: 'Extensive warehousing facilities trimurthy tent house services provider supplier dealer in bangalore' },
  { id: 2, category: 'Warehousing', image: '/IMG-20250802-WA0080.jpg', alt: 'Quality control and maintenance of vast warehouses trimurthy tent house supplier dealer in bangalore' },
  { id: 3, category: 'Warehousing', image: '/IMG-20250802-WA0081.jpg', alt: 'Warehousing facility with quality control trimurthy tent house bangalore karnataka tenthouse supplier' },
  { id: 4, category: 'Warehousing', image: '/IMG-20250802-WA0082.jpg', alt: 'Warehouse trimurthy tent house bangalore tent house supplier and dealer in bangalore' },
  { id: 5, category: 'Warehousing', image: '/IMG-20250802-WA0083.jpg', alt: 'Warehousing facility trimurthy tent house bangalore tent house dealer supplier' },
  
  // Exhibitions
  { id: 6, category: 'Exhibitions', image: '/IMG-20250802-WA0084.jpg', alt: 'Exhibition stalls for rent in bangalore for events and exhibitions trimurthy tent house' },
  { id: 7, category: 'Exhibitions', image: '/IMG-20250802-WA0085.jpg', alt: 'Octanorm exhibition stalls for rent for hire in bangalore for events and exhibitions trimurthy tent house' },
  { id: 8, category: 'Exhibitions', image: '/IMG-20250802-WA0086.jpg', alt: 'German tents and synthetic carpets for rent in bangalore trimurthy tent house' },
  { id: 9, category: 'Exhibitions', image: '/IMG-20250802-WA0087.jpg', alt: 'Exhibition stalls for rent for hire in bangalore trimurthy tent house' },
  { id: 10, category: 'Exhibitions', image: '/IMG-20250802-WA0088.jpg', alt: 'Exhibitions stalls on rent for events and exhibitions bangalore trimurthy tent house' },
  { id: 11, category: 'Exhibitions', image: '/IMG-20250802-WA0089.jpg', alt: 'Exhibition stalls for rent for hire for events and exhibitions in bangalore trimurthy tent house' },
  { id: 12, category: 'Exhibitions', image: '/IMG-20250802-WA0090.jpg', alt: 'Exhibition stalls for rent in bangalore from trimurthy hi tech tent house' },
  { id: 13, category: 'Exhibitions', image: '/IMG-20250802-WA0091.jpg', alt: 'Octanorm exhibition stalls for rent for hire for events tradeshows exhibitions in bangalore trimurthy tent house' },
  { id: 14, category: 'Exhibitions', image: '/IMG-20250802-WA0092.jpg', alt: 'Exhibition stalls for rent for hire in bangalore contact trimurthy tent house bangalore karnataka' },
  { id: 15, category: 'Exhibitions', image: '/IMG-20250802-WA0093.jpg', alt: 'Exhibition stalls and synthetic red carpets for rent in bangalore trimurthy tent house' },
  
  // Events
  { id: 16, category: 'Events', image: '/IMG-20250802-WA0055.jpg', alt: 'Super structures mega hangers tents for rent in bangalore trimurthy tent house' },
  { id: 17, category: 'Events', image: '/IMG-20250802-WA0056.jpg', alt: 'Round tables and chairs for rent for hire in bangalore trimurthy tent house' },
  { id: 18, category: 'Events', image: '/IMG-20250802-WA0057.jpg', alt: 'Waterproof sheds industrial sheds for rent for hire in bangalore trimurthy tent house' },
  { id: 19, category: 'Events', image: '/IMG-20250802-WA0058.jpg', alt: 'Led walls and led lighting for rent for hire for events exhibitions bangalore trimurthy tent house' },
  { id: 20, category: 'Events', image: '/IMG-20250802-WA0059.jpg', alt: 'Aluminium tents for rent for hire in bangalore trimurthy tent house' },
  { id: 21, category: 'Events', image: '/IMG-20250802-WA0060.jpg', alt: 'Golden queue stands with red ribbons for rent for hire in bangalore trimurthy tent house' },
  { id: 22, category: 'Events', image: '/IMG-20250802-WA0061.jpg', alt: 'Ac german hanger tent for rent for hire in bangalore trimurthy tent house' },
  { id: 23, category: 'Events', image: '/IMG-20250802-WA0062.jpg', alt: 'German tents for rent for hire in bangalore trimurthy tent house' },
  { id: 24, category: 'Events', image: '/IMG-20250802-WA0063.jpg', alt: 'German tents aluminium tents for rent in bangalore trimurthy tent house' },
  { id: 25, category: 'Events', image: '/IMG-20250802-WA0064.jpg', alt: 'German hangers super structures tents for rent for hire in bangalore trimurthy tent house' },
  
  // Weddings
  { id: 26, category: 'Weddings', image: '/IMG-20250802-WA0065.jpg', alt: 'Tables and chairs for rent for weddings in bangalore trimurthy tent house' },
  { id: 27, category: 'Weddings', image: '/IMG-20250802-WA0066.jpg', alt: 'Banquet tables chairs tents for rent for weddings in bangalore trimurthy tent house' },
  { id: 28, category: 'Weddings', image: '/IMG-20250802-WA0067.jpg', alt: 'Round tables and chairs with cloth covering for rent for wedding functions in bangalore trimurthy tent house' },
  { id: 29, category: 'Weddings', image: '/IMG-20250802-WA0068.jpg', alt: 'Tent houses for rent for weddings and wedding functions in bangalore trimurthy tent house' },
  { id: 30, category: 'Weddings', image: '/IMG-20250802-WA0069.jpg', alt: 'Tents tables chairs carpets fans air coolers acs for rent for weddings in bangalore trimurthy tent house' },
  { id: 31, category: 'Weddings', image: '/IMG-20250802-WA0070.jpg', alt: 'Tent houses for rent for weddings and wedding functions from trimurthy tent house in bangalore' },
  { id: 32, category: 'Weddings', image: '/IMG-20250802-WA0071.jpg', alt: 'Tents canopies shamianas tables chairs carpets for rent for weddings in bangalore trimurthy tent house' },
  { id: 33, category: 'Weddings', image: '/IMG-20250802-WA0072.jpg', alt: 'Tent houses supplier dealer for weddings and wedding functions in bangalore trimurthy tent house' },
  { id: 34, category: 'Weddings', image: '/IMG-20250802-WA0073.jpg', alt: 'Shamiyanas canopies tables chairs wedding chairs wooden stage for rent in bangalore trimurthy tent house' },
  { id: 35, category: 'Weddings', image: '/IMG-20250802-WA0074.jpg', alt: 'Banquet tables and chairs for rent for weddings in bangalore trimurthy tent house' },
  
  // German Hangers
  { id: 36, category: 'German Hangers', image: '/IMG-20250802-WA0021.jpg', alt: 'German hanger tent structure for large events and exhibitions trimurthy tent house bangalore' },
  { id: 37, category: 'German Hangers', image: '/IMG-20250802-WA0024.jpg', alt: 'Interior view of german hanger tent with professional setup trimurthy tent house' },
  { id: 38, category: 'German Hangers', image: '/IMG-20250802-WA0025.jpg', alt: 'German hanger tent ready for events with complete infrastructure trimurthy tent house' },
  { id: 39, category: 'German Hangers', image: '/IMG-20250802-WA0027.jpg', alt: 'German hanger tent with advanced lighting and sound systems trimurthy tent house' },
  { id: 40, category: 'German Hangers', image: '/IMG-20250802-WA0028.jpg', alt: 'Custom german hanger tent setup for special events trimurthy tent house' },
  { id: 41, category: 'German Hangers', image: '/IMG-20250802-WA0030.jpg', alt: 'German hanger tent perfect for large scale exhibitions trimurthy tent house' },
  { id: 42, category: 'German Hangers', image: '/IMG-20250802-WA0031.jpg', alt: 'German hanger tent with concert and live performance setup trimurthy tent house' },
  { id: 43, category: 'German Hangers', image: '/IMG-20250802-WA0032.jpg', alt: 'German hanger tent for sports events and activities trimurthy tent house' },
  { id: 44, category: 'German Hangers', image: '/IMG-20250802-WA0033.jpg', alt: 'German hanger tent for festivals and cultural events trimurthy tent house' },
  { id: 45, category: 'German Hangers', image: '/IMG-20250802-WA0034.jpg', alt: 'German hanger tent for trade fairs and business events trimurthy tent house' },
  { id: 46, category: 'German Hangers', image: '/IMG-20250802-WA0044.jpg', alt: 'German hanger tent with comfortable seating arrangements trimurthy tent house' },
  { id: 47, category: 'German Hangers', image: '/IMG-20250802-WA0045.jpg', alt: 'German hanger tent with integrated stage for performances trimurthy tent house' },
  { id: 48, category: 'German Hangers', image: '/IMG-20250802-WA0047.jpg', alt: 'German hanger tent with professional event setup trimurthy tent house' },
  { id: 49, category: 'German Hangers', image: '/IMG-20250802-WA0060.jpg', alt: 'German hanger tent for special celebrations and events trimurthy tent house' },
  { id: 50, category: 'German Hangers', image: '/IMG-20250802-WA0065.jpg', alt: 'German hanger tent with premium event infrastructure trimurthy tent house' },
  { id: 51, category: 'German Hangers', image: '/IMG-20250802-WA0070.jpg', alt: 'German hanger tent for grand celebrations and functions trimurthy tent house' },
  { id: 52, category: 'German Hangers', image: '/IMG-20250802-WA0037.jpg', alt: 'German hanger tent with beautiful interior decoration trimurthy tent house' },
  { id: 53, category: 'German Hangers', image: '/gallery_heropage.jpg', alt: 'Gallery showcase of german hanger tents and structures trimurthy tent house' },
  { id: 54, category: 'German Hangers', image: '/IMG-20250802-WA0041.jpg', alt: 'Exterior view of german hanger tent structure trimurthy tent house' },
  { id: 55, category: 'German Hangers', image: '/IMG-20250802-WA0042.jpg', alt: 'German hanger tent illuminated at night for events trimurthy tent house' },
  { id: 56, category: 'German Hangers', image: '/IMG-20250802-WA0043.jpg', alt: 'German hanger tent setup process and construction trimurthy tent house' }
];

// Testimonials data
const testimonialsData = [
  {
    id: 1,
    text: "We have purchased liner, frame tents, and pole tents from Trimurthy Tents and have been very pleased with all the above. Most of their customer service and turn-around-time has been better than any other company we have dealt with.",
    author: "Ravi Kishan",
    date: "2 days ago"
  },
  {
    id: 2,
    text: "Amazing party and, as always, everything from the rentals to the service was above and beyond! Thank you, thank you, thank you!!",
    author: "Anil Kumar",
    date: "4 days ago"
  },
  {
    id: 3,
    text: "We have purchased liner, frame tents, and pole tents from Trimurthy Tents and have been very pleased with all the above. Most of their customer service and turn-around-time has been better than any other company we have dealt with.",
    author: "Prathik Jain",
    date: "1 day ago"
  }
];

// Stats data
const stats = [
  {
    icon: 'far fa-edit',
    target: 145,
    title: 'Exhibitions Completed'
  },
  {
    icon: 'fas fa-building',
    target: 500,
    title: 'Marriage Arranged'
  },
  {
    icon: 'far fa-edit',
    target: 1015,
    title: 'Events Completed'
  },
  {
    icon: 'far fa-smile',
    target: 2500,
    title: 'Happy Customers'
  }
];

// Why Choose data
const whyChooseData = [
  {
    icon: '🏆',
    title: '36 Years of Excellence',
    description: 'Three decades of experience in tent solutions and event management, making us the most trusted name in Bangalore.'
  },
  {
    icon: '⚡',
    title: 'Quick Setup & Delivery',
    description: 'Fast and efficient tent setup and delivery services to meet your urgent event requirements.'
  },
  {
    icon: '💰',
    title: 'Competitive Pricing',
    description: 'Best quality tents and services at the most competitive prices in the market.'
  },
  {
    icon: '🛡️',
    title: 'Quality Assurance',
    description: 'Premium quality materials and professional workmanship ensuring your events are safe and beautiful.'
  },
  {
    icon: '📞',
    title: '24/7 Support',
    description: 'Round-the-clock customer support to handle your queries and emergency requirements.'
  },
  {
    icon: '🎯',
    title: 'Custom Solutions',
    description: 'Tailored tent solutions designed specifically for your unique event requirements and preferences.'
  }
];

const Home = () => {
  const [counters, setCounters] = useState([0, 0, 0, 0]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState('All');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [enquiryData, setEnquiryData] = useState({
    user_name: '',
    user_email: '',
    user_number: '',
    user_subject: '',
    user_message: ''
  });

  // Hero slider auto-slide
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(timer);
  }, []);

  // Counter animation
  useEffect(() => {
    const timers = [];
    let animationStarted = false;

    if (!animationStarted) {
      animationStarted = true;
      
      stats.forEach((stat, index) => {
        const timer = setInterval(() => {
          setCounters(prev => {
            const newCounters = [...prev];
            if (newCounters[index] < stat.target) {
              newCounters[index] = Math.min(newCounters[index] + Math.ceil(stat.target / 100), stat.target);
            } else {
              clearInterval(timer);
            }
            return newCounters;
          });
        }, 20);
        
        timers.push(timer);
      });
    }

    return () => {
      timers.forEach(timer => clearInterval(timer));
    };
  }, []);

  // Testimonials carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonialsData.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuccess(true);
    setFormData({ name: '', email: '', phone: '', message: '' });
    
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  const handleEnquiryInputChange = (e) => {
    setEnquiryData({
      ...enquiryData,
      [e.target.name]: e.target.value
    });
  };

  const handleEnquirySubmit = (e) => {
    e.preventDefault();
    // Handle enquiry form submission
    console.log('Enquiry submitted:', enquiryData);
    setEnquiryData({
      user_name: '',
      user_email: '',
      user_number: '',
      user_subject: '',
      user_message: ''
    });
  };

  return (
    <>
      <SEO
        title="Trimurthy Tent House - Premium Tent Solutions & Event Management in Bangalore"
        description="Trusted 36-year-old tent house in Bangalore offering German tents, aluminium hangers, pagoda tents and canopies rentals for events. Call 88808 28698."
        keywords="Tent House Supplier in Bangalore, Tent House Dealer in Bangalore, Tent House in Bangalore, German Hangers for Rent in Bangalore, German Tents for Rent in Bangalore, Event Tent Rentals in Bangalore, Aluminium Hanger Tent, Pagoda Tents in Bangalore, Canopies for Rent in Bangalore"
        canonical="https://ttheventorganisers.in/"
      />
      <HeroSection>
        <HeroBackground currentImage={heroImages[heroImageIndex]} isActive={true} />
        <SliderNavigation>
          {heroImages.map((_, index) => (
            <SliderDot
              key={index}
              isActive={index === heroImageIndex}
              onClick={() => setHeroImageIndex(index)}
            />
          ))}
        </SliderNavigation>

        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTitle>Welcome to Trimurthy Tent House</HeroTitle>
            <HeroSubtitle>
              Your trusted partner for premium tent solutions, event management, and exhibition services in Bangalore.
            </HeroSubtitle>
          </motion.div>
        </HeroContent>
      </HeroSection>

      <Section style={{ background: '#f9f8f8' }}>
        <Container>
          <SectionTitle>What We Do</SectionTitle>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p style={{ 
              fontSize: '1.4rem', 
              textAlign: 'center', 
              maxWidth: '1000px', 
              margin: '0 auto 40px',
              lineHeight: 1.8,
              color: '#333',
              fontWeight: '500'
            }}>
              Trimurthy Tent House is a trusted 36-year old Tent House Dealer and Tent House Supplier in Bangalore, 
              specializing in premium German Hangers and high-quality tent solutions. We are the leading provider of 
              German Hangers, German tents, Aluminium Hanger tents, and super structures for large-scale events. 
              Our comprehensive services include chairs, canopies, aluminium tents, shamianas, shamiyana tents, AC tents, 
              pagoda tents, exhibition stalls, Octanorm exhibition stalls, mega hangers, wooden stages, pandals, 
              and waterproof sheds for rent for functions, exhibitions, events, conventions, and convention centers 
              in and around Bangalore, Karnataka. Our German Hangers are perfect for corporate events, exhibitions, 
              trade shows, and large gatherings requiring spacious, professional tent structures.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h5 style={{ 
              fontFamily: 'initial', 
              textAlign: 'center', 
              marginBottom: '30px',
              color: '#1a4d80',
              fontSize: '1.3rem',
              fontWeight: '600'
            }}>
              Tent House Supplier services provided by Trimurthy Tent House in Bangalore.
            </h5>
          </motion.div>
          
          <ServicesGrid>
            <ServicesColumn
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                viewport={{ once: true }}
              >
                German Hangers for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                viewport={{ once: true }}
              >
                Steel tubular sheds for warehousing
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Stage made of both wood and steel
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                viewport={{ once: true }}
              >
                Barricading for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                viewport={{ once: true }}
              >
                Backdrops for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.7 }}
                viewport={{ once: true }}
              >
                Carpets for rent 
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.7 }}
                viewport={{ once: true }}
              >
                Round Tables for rent 
              </ServiceItem>

            </ServicesColumn>
            
            <ServicesColumn
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                viewport={{ once: true }}
              >
                Arabian Tents for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Super Structures for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                viewport={{ once: true }}
              >
                German Sheds for rent 
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                viewport={{ once: true }}
              >
                Mattress & Cots for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.7 }}
                viewport={{ once: true }}
              >
                Vessels Banquet Chairs for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.8 }}
                viewport={{ once: true }}
              >
                Chair Hire for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.9 }}
                viewport={{ once: true }}
              >
                Table Rental
              </ServiceItem>
            </ServicesColumn>
            
            <ServicesColumn
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                viewport={{ once: true }}
              >
                China Tent for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                viewport={{ once: true }}
              >
                Pipe Shamiyanas for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                viewport={{ once: true }}
              >
                German Tent for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.7 }}
                viewport={{ once: true }}
              >
                Canopies for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.8 }}
                viewport={{ once: true }}
              >
                Shamianas for rent
              </ServiceItem>
              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.8 }}
                viewport={{ once: true }}
              >
                Sheet Sheds for rent
              </ServiceItem>

              <ServiceItem
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 1.0 }}
                viewport={{ once: true }}
              >
                Furniture Rental
              </ServiceItem>
            </ServicesColumn>
          </ServicesGrid>
        </Container>
      </Section>

      <WhyChooseSection>
        <Container>
          <SectionTitle>
            Why Choose Trimurthy Tent House
          </SectionTitle>
        </Container>
        <WhyChooseContainer>
          <TimelineContainer>
            <TimelineItem 
              initial={{opacity:0, x:-50, scale:0.8}} 
              whileInView={{opacity:1, x:0, scale:1}} 
              transition={{duration:0.6, delay:0.1, type:"spring", stiffness:100}} 
              viewport={{once:true}}
            >
              <TimelineContent>
                <TimelineTitle>36 Years of Excellence</TimelineTitle>
                <TimelineDescription>Three decades of trusted service in Bangalore.</TimelineDescription>
              </TimelineContent>
              <TimelineDot></TimelineDot>
            </TimelineItem>
            
            <TimelineItem 
              initial={{opacity:0, x:-50, scale:0.8}} 
              whileInView={{opacity:1, x:0, scale:1}} 
              transition={{duration:0.6, delay:0.2, type:"spring", stiffness:100}} 
              viewport={{once:true}}
            >
              <TimelineContent>
                <TimelineTitle>Premium German Hangers</TimelineTitle>
                <TimelineDescription>Imported German Hangers with superior quality and durability.</TimelineDescription>
              </TimelineContent>
              <TimelineDot></TimelineDot>
            </TimelineItem>
            
            <TimelineItem 
              initial={{opacity:0, x:-50, scale:0.8}} 
              whileInView={{opacity:1, x:0, scale:1}} 
              transition={{duration:0.6, delay:0.3, type:"spring", stiffness:100}} 
              viewport={{once:true}}
            >
              <TimelineContent>
                <TimelineTitle>24/7 Customer Support</TimelineTitle>
                <TimelineDescription>Our dedicated team is always available for assistance.</TimelineDescription>
              </TimelineContent>
              <TimelineDot></TimelineDot>
            </TimelineItem>
            
            <TimelineItem 
              initial={{opacity:0, x:-50, scale:0.8}} 
              whileInView={{opacity:1, x:0, scale:1}} 
              transition={{duration:0.6, delay:0.4, type:"spring", stiffness:100}} 
              viewport={{once:true}}
            >
              <TimelineContent>
                <TimelineTitle>Custom Event Solutions</TimelineTitle>
                <TimelineDescription>Tailored solutions for every event requirement.</TimelineDescription>
              </TimelineContent>
              <TimelineDot></TimelineDot>
            </TimelineItem>
          </TimelineContainer>

          <ImageContainer>
            <WhyChooseImage 
              src="/IMG-20250802-WA0077.jpg" 
              alt="Trimurthy Tent House" 
            />
          </ImageContainer>
        </WhyChooseContainer>
      </WhyChooseSection>

      <StatsTestSection className="stats-section">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <SectionTitle style={{ color: "white", marginBottom: "60px" }}>
              Our Achievements
            </SectionTitle>
          </motion.div>
          <StatsRow className="inner_stat_wthree_agileits">
            {stats.map((stat, index) => (
              <StatsCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <StatsContent>
                  <i className={stat.icon}></i>
                  <div className="counter">{counters[index]}</div>
                  <h4>{stat.title}</h4>
                </StatsContent>
              </StatsCard>
            ))}
          </StatsRow>
        </Container>
      </StatsTestSection>

      <GallerySection>
        <Container>
          <GalleryTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Gallery
          </GalleryTitle>

          <FilterContainer className="portfolio-categ filter">
            <FilterItem className={`port-filter all ${activeFilter === 'All' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('All');
                }}
              >
                All
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-1 ${activeFilter === 'Events' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 1"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('Events');
                }}
              >
                Events
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-2 ${activeFilter === 'Exhibitions' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 2"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('Exhibitions');
                }}
              >
                Exhibitions
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-3 ${activeFilter === 'Weddings' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 3"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('Weddings');
                }}
              >
                Weddings
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-4 ${activeFilter === 'Warehousing' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 4"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('Warehousing');
                }}
              >
                Warehousing
              </FilterLink>
            </FilterItem>
            <FilterItem className={`cat-item-5 ${activeFilter === 'German Hangers' ? 'active' : ''}`}>
              <FilterLink 
                href="#" 
                title="Category 5"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveFilter('German Hangers');
                }}
              >
                German Hangers
              </FilterLink>
            </FilterItem>
          </FilterContainer>

          <PortfolioArea className="portfolio-area">
            {galleryData
              .filter(item => activeFilter === 'All' || item.category === activeFilter)
              .map((item, index) => (
                <PortfolioItem
                  key={item.id}
                  className="portfolio-item2 mb-2"
                  data-id={`id-${index}`}
                  data-type={`cat-item-${item.category === 'Events' ? '1' : item.category === 'Exhibitions' ? '2' : item.category === 'Weddings' ? '3' : item.category === 'Warehousing' ? '4' : item.category === 'German Hangers' ? '5' : '1'}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div>
                    <ImageBlock className="image-block img-hover">
                      <ImageZoom 
                        className="image-zoom" 
                        href={item.image}
                      >
                        <img 
                          src={item.image} 
                          alt={item.alt} 
                          title={item.alt} 
                          className="img-responsive"
                        />
                        <div className="port-info">
                          <h5>{item.category}</h5>
                          <p>Trimurthy Tent House</p>
                        </div>
                      </ImageZoom>
                    </ImageBlock>
                  </div>
                </PortfolioItem>
              ))}
          </PortfolioArea>
        </Container>
      </GallerySection>



      {/* Additional Testimonials Section from Services Page */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        style={{
          background: '#1b1b1b',
          padding: '80px 0',
          color: 'white'
        }}
      >
        <Container>
          <motion.h3
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            style={{
              textAlign: 'center',
              textTransform: 'uppercase',
              marginBottom: '60px',
              fontSize: '2.5rem',
              fontWeight: '600',
              color: 'white'
            }}
          >
            Testimonials
          </motion.h3>
          
          <div style={{ position: 'relative', maxWidth: '900px', margin: '0 auto' }}>
            {/* Carousel Container */}
            <div style={{ 
              overflow: 'hidden', 
              position: 'relative',
              borderRadius: '0',
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px 20px'
            }}>
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                style={{ textAlign: 'center' }}
              >
                <div style={{ marginBottom: '30px' }}>
                  <FiMessageSquare 
                    style={{ 
                      fontSize: '2rem', 
                      color: '#1a4d80', 
                      marginBottom: '20px',
                      display: 'block',
                      margin: '0 auto 20px'
                    }} 
                  />
                  <p style={{
                    fontSize: '1.2rem',
                    lineHeight: '1.8',
                    marginBottom: '30px',
                    fontStyle: 'italic',
                    color: '#f8f9fa'
                  }}>
                    {testimonialsData[currentIndex].text}
                  </p>
                  <FiMessageSquare 
                    style={{ 
                      fontSize: '2rem', 
                      color: '#1a4d80',
                      display: 'block',
                      margin: '20px auto 0'
                    }} 
                  />
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <h5 style={{
                    fontSize: '1.3rem',
                    fontWeight: '600',
                    marginBottom: '10px',
                    color: '#1a4d80'
                  }}>
                    {testimonialsData[currentIndex].author}
                  </h5>
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#adb5bd',
                    margin: '0'
                  }}>
                    {testimonialsData[currentIndex].date}
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentIndex(prevIndex => 
                prevIndex === 0 ? testimonialsData.length - 1 : prevIndex - 1
              )}
              style={{
                position: 'absolute',
                left: '-50px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#1a4d80',
                border: 'none',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                transition: 'all 0.3s ease',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#2c5aa0';
                e.target.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#1a4d80';
                e.target.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <FiChevronLeft />
            </button>

            <button
              onClick={() => setCurrentIndex(prevIndex => 
                prevIndex === testimonialsData.length - 1 ? 0 : prevIndex + 1
              )}
              style={{
                position: 'absolute',
                right: '-50px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#1a4d80',
                border: 'none',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                transition: 'all 0.3s ease',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#2c5aa0';
                e.target.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#1a4d80';
                e.target.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <FiChevronRight />
            </button>

            {/* Dots Navigation */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              marginTop: '30px'
            }}>
              {testimonialsData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '0',
                    border: 'none',
                    background: index === currentIndex ? '#1a4d80' : '#6c757d',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (index !== currentIndex) {
                      e.target.style.background = '#adb5bd';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (index !== currentIndex) {
                      e.target.style.background = '#6c757d';
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </Container>
      </motion.section>

      {/* Enquiry Form from Contact Page */}
      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <EnquiryForm>
              <h3 style={{
                color: '#1a4d80',
                textAlign: 'center',
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '30px',
                marginTop: '0'
              }}>Enquiry Form</h3>
              <form action="" method="post" id="first_form" name="enquiryform" onSubmit={handleEnquirySubmit}>
                <FormField>
                  <EnquiryInput 
                    placeholder="Your name" 
                    type="text" 
                    name="user_name" 
                    value={enquiryData.user_name}
                    onChange={handleEnquiryInputChange}
                    tabIndex="1"
                  />
                </FormField>
                <FormField>
                  <EnquiryInput 
                    placeholder="Your Email Address" 
                    name="user_email" 
                    type="email" 
                    value={enquiryData.user_email}
                    onChange={handleEnquiryInputChange}
                    tabIndex="2"
                  />
                </FormField>
                <FormField>
                  <EnquiryInput 
                    placeholder="Your Phone Number" 
                    name="user_number" 
                    type="tel" 
                    value={enquiryData.user_number}
                    onChange={handleEnquiryInputChange}
                    tabIndex="3"
                  />
                </FormField>

                <FormField>
                  <EnquiryTextArea 
                    placeholder="Type your message here...." 
                    name="user_message" 
                    value={enquiryData.user_message}
                    onChange={handleEnquiryInputChange}
                    tabIndex="4"
                  />
                </FormField>
                <FormField>
                  <EnquirySubmitButton 
                    name="submit" 
                    type="submit" 
                    id="submit" 
                    data-submit="...Sending"
                  >
                    Submit
                  </EnquirySubmitButton>
                </FormField>
                <EnquirySuccessMessage className="copyright" id="success" />
              </form>
            </EnquiryForm>
          </motion.div>
        </Container>
      </Section>


    </>
  );
};

export default Home; 